import FilterIcon from "@mui/icons-material/FilterList";
import ReorderIcon from "@mui/icons-material/Reorder";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, Popover, Toolbar, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { ScreenBreakpoint } from "@crochik/pi-api";
import { IActionMenuItem } from "src/pi/context/IMenu";
import { DataView } from "../../DataView";
import ActionMenuBar from "../ActionMenuBar";
import { SearchInput } from "../DataGridComponent/SearchInput";
import { FieldsComponent } from "../DataViewComponent/FieldsComponent";

interface IDataViewToolbarComponentProps {
  view: DataView;
  headerComponent?: React.ReactNode;
  args?: any[];
  onAction?: (action: IActionMenuItem, event: React.MouseEvent) => any;
}

export default function DataViewToolbarComponent(
  props: IDataViewToolbarComponentProps,
) {
  const { view, headerComponent, args, onAction } = props;
  const {
    isFilterVisible,
    search,
    breakpoint,
    viewType,
    isViewFilterable,
    filterVisible,
    menu,
  } = view;
  const [reorderParent, setReorderParent] = useState<Element>();
  const [searchParent, setSearchParent] = useState<Element>();

  const filter = useCallback(
    (criteria?: string) => {
      if (!view) return;
      view.searchOrFilter(criteria, args);
      if (criteria) {
        setSearchParent(undefined);
      }
    },
    [view, args],
  );

  const onToggleFilter = useCallback(() => {
    if (!view) return;
    view.filterVisible = !filterVisible;
  }, [filterVisible, view]);

  const toggleReorder = useCallback(
    (event: React.MouseEvent) => {
      if (!view) return;

      if (!!reorderParent) {
        setReorderParent(undefined);
        const _ = view.onColumnsChangedAsync();
      } else {
        setReorderParent(event?.target as Element);
      }
    },
    [view, reorderParent],
  );

  const toggleSearch = useCallback(
    (event: React.MouseEvent) => {
      if (!view) return;

      if (!!searchParent) {
        setSearchParent(undefined);
      } else {
        setSearchParent(event?.target as Element);
      }
    },
    [view, searchParent],
  );

  const searchComponent = useCallback(() => {
    if (!isFilterVisible) return null;

    if (breakpoint === ScreenBreakpoint.Xs || viewType === "Card") {
      return (
        <IconButton
          onClick={toggleSearch}
          color={searchParent || view.search ? "primary" : "default"}
        >
          <SearchIcon />
        </IconButton>
      );
    }

    return (
      <SearchInput
        variant="standard"
        label={view.isSearchable ? "Search" : "Filter"}
        value={search || ""}
        onSearch={filter}
        style={{ width: "150px", marginLeft: headerComponent ? 16 : undefined }}
        collapsible={true}
        disabled={view.isLoading}
      />
    );
  }, [
    isFilterVisible,
    breakpoint,
    viewType,
    view.isSearchable,
    view.isLoading,
    view.search,
    search,
    filter,
    headerComponent,
    toggleSearch,
    searchParent,
  ]);

  const filterComponent = useCallback(() => {
    if (!isViewFilterable) return null;
    return (
      <IconButton
        key="filter"
        color={filterVisible ? "primary" : "inherit"}
        onClick={onToggleFilter}
        title="Filter"
      >
        <FilterIcon />
      </IconButton>
    );
  }, [isViewFilterable, filterVisible, onToggleFilter]);

  const reorderComponent = useCallback(() => {
    if (viewType === "Calendar") return null;
    return (
      <IconButton
        onClick={toggleReorder}
        color={reorderParent ? "primary" : "default"}
      >
        <ReorderIcon />
      </IconButton>
    );
  }, [viewType, toggleReorder, reorderParent]);

  const menuComponent = useCallback(() => {
    if (!menu) return null;
    return (
      <ActionMenuBar menu={menu} context={view.context} onAction={onAction} />
    );
  }, [menu, onAction, view.context]);

  return (
    <>
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          // backgroundColor: "#fff",
          zIndex: 1,
          position: "sticky",
          top: "0px",
          left: "0px",
          width: "100%",
          justifyContent: {
            xs: "center",
            sm: "space-between",
          },
        }}
      >
        <Box
          sx={{
            display: { xs: "none", sm: "block" },
          }}
        >
          <Typography variant="h6" color="primary" id="tableTitle">
            {view.title}
          </Typography>
        </Box>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {headerComponent}
          {searchComponent()}
          {filterComponent()}
          {reorderComponent()}
          {menuComponent()}
        </div>
      </Toolbar>
      <Popover
        open={!!reorderParent}
        anchorEl={reorderParent}
        onClose={toggleReorder}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <div
          style={{
            maxHeight: "600px",
            maxWidth: "600px",
            overflow: "hidden",
            padding: "12px",
            backgroundColor: "#efefef",
          }}
        >
          <FieldsComponent dataView={view} />
        </div>
      </Popover>
      <Popover
        open={!!searchParent}
        anchorEl={searchParent}
        onClose={toggleSearch}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <div
          style={{
            maxHeight: "600px",
            maxWidth: "600px",
            overflow: "auto",
            padding: "12px",
            backgroundColor: "#efefef",
          }}
        >
          <SearchInput
            variant="standard"
            label={view.isSearchable ? "Search" : "Filter"}
            // placeholder={view.isSearchable ? "Search..." : "Filter..."}
            value={search || ""}
            onSearch={filter}
            collapsible={false}
            disabled={view.isLoading}
          />
        </div>
      </Popover>
    </>
  );
}
